// @ts-check
// See README.md for more info about field configuration

import {
  STREAM,
  EA_TAB_FILTERING,
  FIELD_TYPE,
  SUPPORTING_DOCS,
  USER_GROUP
} from '@/constants'
import store from '@/store/store'

/** @typedef {import('../types/supportingDocuments').SupportingDoc} SupportingDoc */
/** @typedef {import('../types/supportingDocuments').FilterFunction} FilterFunction */
/** @typedef {import('../types/supportingDocuments').FileField} FileField */
/** @typedef {import('../types/supportingDocuments').HeadingField} HeadingField */
/** @typedef {import('../types/supportingDocuments').CollectionField} CollectionField */

/**
 * @param {SupportingDoc} item
 * @returns {boolean}
 */
const isAdditionalDocs = (item) => item?.category === 'additionalDocuments'
const isNotAdditionalDocs = (item) => !isAdditionalDocs(item)
const isOesAdmin = (store) => store?.state?.userGroup === USER_GROUP.ERN_ADMIN
const isArchived = (store) => store?.state?.currentStream === STREAM.ARCHIVE
const isVirtualSelective = (store) => store?.getters?.isVirtualSelectiveSchool
const shouldShowAdditionalDocs = (application) => {
  const hasAdditionalDocs =
    application?.supportingDocuments?.additionalDocuments?.length > 0
  return (
    (isPendingTabEnrolment(application) && !isArchived(store)) ||
    hasAdditionalDocs
  )
}

/**
 * Generates document fields based on supporting documents
 * @param {SupportingDoc[]} items
 * @param {function(*): boolean} [filterFn=(item) => true]
 * @param {function(*): boolean} [displayHeadingFn=() => true]
 * @returns {Array<HeadingField | CollectionField>}
 */
export const generateDocsFields = (
  items,
  filterFn = () => true,
  displayHeadingFn = () => true
) => {
  return items.filter(filterFn).reduce((cur, i) => {
    cur.push(
      {
        label: i.label,
        type: FIELD_TYPE.HEADING,
        visible(application) {
          return displayHeadingFn(application)
        }
      },
      {
        apiKey: 'supportingDocuments.' + i.category,
        type: FIELD_TYPE.COLLECTION,
        showNone: isNotAdditionalDocs(i),
        fields(apiPrefix, fileItem) {
          return [
            {
              apiKey: apiPrefix + 'objectKey',
              colspan: 3,
              type: FIELD_TYPE.FILE_EDITOR,
              handleView(value) {
                const showOrDownloadDocument = store.dispatch(
                  'showOrDownloadDocument',
                  value
                )

                if (
                  isAdditionalDocs(i) &&
                  !fileItem?.isDocViewed &&
                  !isArchived(store) &&
                  !isVirtualSelective(store)
                ) {
                  return showOrDownloadDocument.then(() => {
                    return store.dispatch('updateAppDocStatus', {
                      docRecordNo: value.docRecordNo,
                      isDocViewed: true
                    })
                  })
                }

                return showOrDownloadDocument
              },
              canView: isOesAdmin(store),
              status: fileItem.status,
              isDisplayNewLabel:
                isAdditionalDocs(i) &&
                isOesAdmin(store) &&
                isPendingTabEnrolment(store?.state?.application),
              isAdditionalDocs: isAdditionalDocs(i),
              docReceived: fileItem?.docReceived,
              docRecordNo: fileItem?.docRecordNo
            }
          ]
        }
      }
    )
    return cur
  }, [])
}

function requireAdditionalDocs(application) {
  return application?.requireAdditionalDocs === true
}

function isPendingTabEnrolment(application) {
  return EA_TAB_FILTERING['PENDING'].includes(application?.applicationStatus)
}

export default [
  {
    message: {
      type: 'warning',
      body: 'Reminder: In circumstances where it is difficult for the parent/carer to provide documents, principals can waive the parent/carer’s requirement to do so.'
    },
    type: FIELD_TYPE.MESSAGE
  },
  {
    message: {
      type: 'info',
      body: 'Downloaded documents should be appropriately destroyed once they have been used for identification purposes.'
    },
    type: FIELD_TYPE.MESSAGE
  },
  {
    type: FIELD_TYPE.CUSTOM,
    component: 'AdsButton',
    componentProps: {
      secondary: true,
      icon: 'mdi-folder-multiple-outline',
      buttonText: 'Download all'
    },
    canView: USER_GROUP.ERN_ADMIN, // I think for CUSTOM field type I can't use "store.state.userGroup === USER_GROUP.ERN_ADMIN"
    cssStyle: 'display: flex; justify-content: flex-end;'
  },
  ...generateDocsFields(SUPPORTING_DOCS, isNotAdditionalDocs),
  {
    type: FIELD_TYPE.CUSTOM,
    component: 'SimpleHr',
    componentProps: {
      color: '#cdd3d6'
    },
    visible(application) {
      return shouldShowAdditionalDocs(application)
    }
  },
  ...generateDocsFields(
    SUPPORTING_DOCS,
    isAdditionalDocs,
    shouldShowAdditionalDocs
  ),
  {
    type: FIELD_TYPE.CUSTOM,
    component: 'SimpleParagraphs',
    componentProps: {
      paragraphs() {
        return [
          `If the attached documents are insufficient, ${
            isVirtualSelective(store)
              ? 'a census school admin user'
              : `${isOesAdmin(store) ? 'you' : 'a school admin user'}`
          } can enable document upload for this application. This allows the parent/carer to upload more documents.`,
          'Newly uploaded documents will appear here.'
        ]
      }
    },
    visible(application) {
      return (
        !requireAdditionalDocs(application) &&
        isPendingTabEnrolment(application) &&
        !isArchived(store)
      )
    },
    readOnly: true
  },
  {
    type: FIELD_TYPE.CUSTOM,
    component: 'AdsButton',
    componentProps: {
      secondary: true,
      icon: 'mdi-upload-outline',
      buttonText: 'Enable document upload'
    },
    visible(application) {
      return (
        !isVirtualSelective(store) &&
        !requireAdditionalDocs(application) &&
        isPendingTabEnrolment(application) &&
        isOesAdmin(store) &&
        !isArchived(store)
      )
    },
    category: 'enable',
    cssStyle: 'margin-left: -8px !important; margin-top: -16px !important;'
  },
  {
    apiKey: 'requireAdditionalDocsByInfo',
    colspan: 3,
    type: FIELD_TYPE.FILE_EDITOR,
    status: 'PENDING-UPLOAD',
    visible(application) {
      return (
        requireAdditionalDocs(application) &&
        isPendingTabEnrolment(application) &&
        !isArchived(store)
      )
    }
  },
  {
    type: FIELD_TYPE.CUSTOM,
    component: 'SimpleParagraphs',
    componentProps: {
      paragraphs() {
        return [
          `If the parent/carer has supplied additional documents in-person or if document upload is no longer required, it can be disabled${
            isVirtualSelective(store)
              ? ' by a census school admin user'
              : `${isOesAdmin(store) ? '' : ' by a school admin user'}`
          }.`
        ]
      }
    },
    visible(application) {
      return (
        requireAdditionalDocs(application) &&
        isPendingTabEnrolment(application) &&
        !isArchived(store)
      )
    },
    readOnly: true
  },
  {
    type: FIELD_TYPE.CUSTOM,
    component: 'AdsButton',
    componentProps: {
      color: 'red',
      secondary: true,
      icon: 'mdi-upload-lock-outline',
      buttonText: 'Disable document upload'
    },
    visible(application) {
      return (
        !isVirtualSelective(store) &&
        requireAdditionalDocs(application) &&
        isPendingTabEnrolment(application) &&
        isOesAdmin(store) &&
        !isArchived(store)
      )
    },
    category: 'disable',
    cssStyle: 'margin-left: -8px !important; margin-top: -16px !important;'
  }
]
